import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, GeoJSON, Polyline, Marker, useMap, ZoomControl } from "react-leaflet";
import { gpx } from "@tmcw/togeojson";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { Loader2, MapPin, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BACKEND_URL } from "../config";
import { getUploadUrl } from "@/lib/utils";
import DirectionArrows from "./trip/DirectionArrows";
import { MapTooltipsContext, MapTooltip, useTooltipToggle } from "./maps/MapTooltipToggle";
import { MapArrowsContext, useArrowsToggle } from "./maps/MapArrowsToggle";
// b99.9.39 — Single source of truth for all map pin icons. Removes
// the previous bespoke startIcon/finishIcon/segmentIcon that DRIFTED
// from RouteMapViewer (24×32 with simple circle/rect instead of the
// canonical 28×36 door/checkered-flag SVG). User requirement:
// "TODOS os mapas têm que ter os mesmos estilos."
import { segmentIcon, dotIcon, hoverIcon, startIcon, finishIcon, circularIcon, poiIcon, POI_CATEGORY_KEYS, POI_CATEGORY_COLORS } from "./maps/mapIcons";
import { useTripAllSegmentsElevation } from "./tripMap/useTripSegmentElevation";
import { SEGMENT_COLORS as SHARED_SEGMENT_COLORS, colorForSegment } from "./tripMap/config";
import { HoverHighlight } from "./maps/RouteShapes";
import { nativeStopsFromGeometry } from "@/lib/routes-tools/routeRenderModel";
// b99.9.82 — Sub-components + config extracted to keep the main file
// focused on state/orchestration. Same code path drives both inline
// (constrained area in the public trip sheet) and fullscreen (full
// viewport via portal); the only differences live in `config.js`.
import { getMapCfg } from "./tripOverviewMap/config";
import TripOverviewControls from "./tripOverviewMap/Controls";
import TripOverviewElevationPanel from "./tripOverviewMap/ElevationPanel";
import TripOverviewHistogramPanel from "./tripOverviewMap/HistogramPanel";

// ─── Palette for segment routes ───
// b99.10.04 — moved to `./tripMap/config.js` so both this view and
// RouteMapViewer share the same color cycle. Re-exported via import.
const SEGMENT_COLORS = SHARED_SEGMENT_COLORS;
// Re-exported helper for inline use.
void colorForSegment;

// b99.10.10 — Tile providers movidos para `mapShell/tileLayers.js` (partilhados
// com RouteMapViewer). `MapTileLayer` é o wrapper React que escolhe o provider
// a partir do `view` prop. Future-proof para adicionar 4.ª camada Híbrida.
import MapTileLayer from "./mapShell/MapTileLayer";

// Returns a display-friendly name, or null if the name is just coordinates.
// b99.9.61 — URL-decode UTF-8 escape sequences. Some Google Maps URLs
// embed names in encoded form ("Le%C3%A7a da Palmeira" → "Leça da
// Palmeira", "S%2FN" → "S/N"); the parser returns them as-is.
function getDisplayName(name) {
  if (!name) return null;
  let s = name.trim();
  if (/%[0-9A-Fa-f]{2}/.test(s)) {
    try { s = decodeURIComponent(s); } catch (e) { /* keep original */ }
  }
  if (/^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/.test(s)) return null;
  return s.split(",")[0].trim() || null;
}

// ─── Utility: extract [lat, lng] from GeoJSON ───
function extractCoords(geoJson) {
  const coords = [];
  if (!geoJson?.features) return coords;
  for (const f of geoJson.features) {
    const g = f.geometry;
    if (!g) continue;
    if (g.type === "LineString") {
      for (const c of g.coordinates) coords.push([c[1], c[0]]);
    } else if (g.type === "MultiLineString") {
      for (const line of g.coordinates) for (const c of line) coords.push([c[1], c[0]]);
    } else if (g.type === "Point") {
      coords.push([g.coordinates[1], g.coordinates[0]]);
    }
  }
  return coords;
}

// ─── Routing & parsing helpers ───
// Both helpers DELEGATE to backend endpoints (b99.9.24 unification).
async function fetchDirections(waypoints) {
  if (!waypoints || waypoints.length < 2) return null;
  try {
    const res = await fetch(`${BACKEND_URL}/api/routes/public/directions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        waypoints: waypoints.map((w) => {
          const out = { lat: w.lat, lng: w.lng };
          if (w.via === true) out.via = true;
          return out;
        }),
        avoid: [],
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (Array.isArray(data.coords) && data.coords.length >= 2) {
      return data.coords;
    }
  } catch { /* fallback */ }
  return null;
}

async function fetchGmapsWaypoints(url) {
  if (!url) return null;
  // b99.10.82 (2026-05-23) — Module-level dedup cache. The Trip Overview
  // Map mounts one <SegmentDataLoader> per segment; if a trip has 10
  // segments and the same Google Maps URL appears in several of them
  // (common for "Volta a X" templates) or even just 10 distinct URLs
  // loaded in parallel on initial render, we'd burn 10 hits against
  // the per-IP rate limit (PUB-RATE-LIMIT-429). We collapse:
  //   - identical concurrent requests → single in-flight promise
  //   - resolved URLs → permanent in-memory cache for the page lifetime
  // No invalidation needed because the parser is pure (URL → waypoints).
  const cache = fetchGmapsWaypoints._cache || (fetchGmapsWaypoints._cache = new Map());
  const inflight = fetchGmapsWaypoints._inflight || (fetchGmapsWaypoints._inflight = new Map());
  if (cache.has(url)) return cache.get(url);
  if (inflight.has(url)) return inflight.get(url);
  const p = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/utils/parse-gmaps-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (Array.isArray(data.waypoints) && data.waypoints.length > 0) {
        return data.waypoints;
      }
    } catch { /* fallback */ }
    return null;
  })();
  inflight.set(url, p);
  try {
    const result = await p;
    cache.set(url, result);
    return result;
  } finally {
    inflight.delete(url);
  }
}

// ─── FitBounds helper ───
// b99.9.67 — Accepts `fullscreen` so inline maps fit with tight
// padding (route fills the small canvas) and fullscreen keeps a
// breath-room padding.
function FitAllBounds({ allCoords, fullscreen = false }) {
  const map = useMap();
  useEffect(() => {
    if (!allCoords || allCoords.length === 0) return;
    const bounds = L.latLngBounds(allCoords.map(c => Array.isArray(c) ? c : [c.lat, c.lng]));
    if (bounds.isValid()) {
      // b99.9.69/.82 — Asymmetric padding so start/end markers and
      // their tooltips have headroom. Values are centralised in
      // `tripOverviewMap/config.js`.
      const cfg = getMapCfg(fullscreen);
      map.fitBounds(bounds, {
        paddingTopLeft: [cfg.padX, cfg.padTop],
        paddingBottomRight: [cfg.padX, cfg.padBottom],
        maxZoom: cfg.maxZoom,
      });
    }
  }, [allCoords, map, fullscreen]);
  return null;
}

// b99.9.56/57 — Refits the map bounds when the container size changes
// (e.g. fullscreen toggle). Mirrors RouteMapViewer's MapResizeHandler.
// b99.9.57 — accepts explicit `fitCoords` so we re-fit to the
// authoritative trip extent even when polylines are still mounting
// (layer-based fitting otherwise misses parts off-screen).
function MapResizeHandler({ fullscreen, fitCoords = null, panelKey = "" }) {
  const map = useMap();
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (firstRunRef.current) { firstRunRef.current = false; return; }
    let cancelled = false;
    const doFit = () => {
      if (cancelled) return;
      map.invalidateSize();
      // b99.9.82 — Read responsive knobs from the central config.
      const cfg = getMapCfg(fullscreen);
      const fitOpts = {
        paddingTopLeft: [cfg.padX, cfg.padTop],
        paddingBottomRight: [cfg.padX, cfg.padBottom],
        maxZoom: cfg.maxZoom,
      };
      try {
        if (Array.isArray(fitCoords) && fitCoords.length > 0) {
          const bounds = L.latLngBounds(
            fitCoords.map((c) =>
              Array.isArray(c) ? [c[0], c[1]] : [c.lat ?? c[0], c.lng ?? c[1]],
            ),
          );
          if (bounds.isValid()) {
            map.fitBounds(bounds, fitOpts);
            return;
          }
        }
        const bounds = L.latLngBounds([]);
        map.eachLayer((layer) => {
          if (typeof layer.getBounds === "function") {
            const lb = layer.getBounds();
            if (lb && lb.isValid && lb.isValid()) bounds.extend(lb);
          } else if (typeof layer.getLatLng === "function") {
            bounds.extend(layer.getLatLng());
          }
        });
        if (bounds.isValid()) map.fitBounds(bounds, fitOpts);
      } catch (e) { /* noqa */ }
    };
    // b99.9.92 — Faster re-fit cadence when toggling the bottom panel
    // so the route snaps to the new viewport immediately. The 350/900
    // pair still covers slow polyline mounts during the initial paint.
    const t1 = setTimeout(doFit, 120);
    const t2 = setTimeout(doFit, 360);
    const t3 = setTimeout(doFit, 900);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [fullscreen, map, fitCoords, panelKey]);
  return null;
}

// ─── OSRM-routed polyline with fallback ───
function RoutedLine({ waypoints, color, opacity = 0.85, compact = false }) {
  const [coords, setCoords] = useState(null);
  const key = useMemo(() => JSON.stringify(waypoints.map(w => [w.lat, w.lng])), [waypoints]);

  useEffect(() => {
    if (!waypoints || waypoints.length < 2) return;
    let cancelled = false;
    fetchDirections(waypoints).then(r => { if (!cancelled && r) setCoords(r); });
    return () => { cancelled = true; };
  }, [key]);

  const positions = coords || waypoints.map(w => [w.lat, w.lng]);
  return (
    <>
      <Polyline positions={positions} pathOptions={{ color, weight: 4, opacity }} />
      {positions.length >= 2 && (
        <DirectionArrows coords={positions} compact={compact} />
      )}
    </>
  );
}

// ─── Load a single segment's route data ───
function useSegmentRoute(segment) {
  const [state, setState] = useState({ loading: true, geoJson: null, coords: [], waypoints: null, type: null });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const gpxFile = segment.gpx_file;
      const gmapsUrl = segment.google_maps_url;
      const locs = (segment.localities || []).filter(l => l.lat && l.lng);

      const rgCoords = segment.route_geometry && Array.isArray(segment.route_geometry.coords)
        ? segment.route_geometry.coords
        : null;

      // b99.10.02 — When the segment has GPX, surface the trip's
      // localities array as the sidebar's "Pontos" list too. Before, GPX
      // segments returned `waypoints: null` and the sidebar said "Sem
      // pontos para listar" even when localities/POIs existed in the DB.
      // The polyline keeps coming from the GPX track; localities only
      // populate the labels on the side panel.
      const sidebarWaypoints = locs.length > 0 ? locs : null;

      // Priority: GPX > Google Maps > Localities
      if (gpxFile) {
        try {
          const url = getUploadUrl(gpxFile);
          const res = await fetch(url);
          if (!res.ok) throw new Error();
          const xml = new DOMParser().parseFromString(await res.text(), "text/xml");
          const gj = gpx(xml);
          const coords = extractCoords(gj);
          if (!cancelled) setState({ loading: false, geoJson: gj, coords, waypoints: sidebarWaypoints, type: "gpx" });
          return;
        } catch { /* fall through */ }
      }

      if (gmapsUrl) {
        if (rgCoords && rgCoords.length > 0) {
          const gj = {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: rgCoords.map(c => [c[1], c[0]]),
            },
          };
          // b99.9.56 — also fetch the waypoints so the sidebar can list
          // the named "Pontos" of this segment. Background, non-blocking.
          const wps = await fetchGmapsWaypoints(gmapsUrl);
          if (!cancelled) setState({
            loading: false,
            geoJson: gj,
            coords: rgCoords.map(c => [c[0], c[1]]),
            waypoints: wps || null,
            type: "precomputed",
          });
          return;
        }
        const wps = await fetchGmapsWaypoints(gmapsUrl);
        if (wps && wps.length > 0 && !cancelled) {
          setState({ loading: false, geoJson: null, coords: [], waypoints: wps, type: "google" });
          return;
        }
      }

      // EDITOR-GEOMETRY-PRIORITY (2026-07-22) — segmentos autorados no Editor
      // (route_type "editor"/"my_routes") guardam a geometria DENSA canónica em
      // `route_geometry.coords` + os WAYPOINTS NATIVOS (kind/role) em
      // `route_geometry.waypoints`. ANTES, sem gpx/gmaps, o mapa só usava as
      // coords quando JÁ existiam `localities` (geocodificadas) — caso
      // contrário não desenhava NADA; e quando existiam, usava-as como
      // waypoints, pondo o INÍCIO/FIM nos centroides dos municípios (errado) e
      // mostrando pontos de estrada como paragens. Agora damos prioridade à
      // geometria real e aos waypoints nativos: a polyline vem das coords, os
      // marcadores início/fim caem nos extremos reais e os pontos de estrada
      // (`kind:"road"`) são marcados `via` (não aparecem como paragens).
      if (rgCoords && rgCoords.length > 0 && !cancelled) {
        const gj = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: rgCoords.map(c => [c[1], c[0]]),
          },
        };
        const nativeWps = nativeStopsFromGeometry(segment.route_geometry);
        const sidebarWps = nativeWps.length > 0 ? nativeWps : (locs.length > 0 ? locs : null);
        setState({
          loading: false,
          geoJson: gj,
          coords: rgCoords.map(c => [c[0], c[1]]),
          waypoints: sidebarWps,
          type: "precomputed",
        });
        return;
      }

      if (locs.length > 0 && !cancelled) {
        if (rgCoords && rgCoords.length > 0) {
          const gj = {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: rgCoords.map(c => [c[1], c[0]]),
            },
          };
          setState({
            loading: false,
            geoJson: gj,
            coords: rgCoords.map(c => [c[0], c[1]]),
            waypoints: locs,
            type: "precomputed",
          });
          return;
        }
        setState({ loading: false, geoJson: null, coords: [], waypoints: locs, type: "localities" });
        return;
      }

      if (!cancelled) setState({ loading: false, geoJson: null, coords: [], waypoints: null, type: null });
    }

    setState({ loading: true, geoJson: null, coords: [], waypoints: null, type: null });
    load();
    return () => { cancelled = true; };
  }, [segment.gpx_file, segment.google_maps_url, segment.segment_id, segment.route_geometry]);

  return state;
}

// ─── Main component: collects all coords for FitBounds ───
function SegmentLayerWithCoords({ segment, color, index, onCoordsReady, opacity = 0.85, hidden = false, skipFirstWaypoint = false, skipLastWaypoint = false, compactMarkers = false, showWaypoints = true, hiddenPoiCats, boundaryKeys = null }) {
  const route = useSegmentRoute(segment);

  useEffect(() => {
    if (route.loading) return;
    let pts = [];
    if (route.coords.length > 0) pts = route.coords;
    else if (route.waypoints) pts = route.waypoints.map(w => [w.lat, w.lng]);
    // b99.9.61 — Also publish the first/last REAL waypoint names (the
    // user wants the start/finish tooltips to show the LOCATION, not
    // the segment/trip title). "Via" waypoints (ponto de forma) are
    // skipped so the start/finish reflects actual stops.
    let startName = null, endName = null;
    if (route.waypoints && route.waypoints.length > 0) {
      const realWps = route.waypoints.filter(w => !(w.via === true || w.isViaPoint === true));
      if (realWps.length > 0) {
        startName = getDisplayName(realWps[0].name);
        if (realWps.length > 1) endName = getDisplayName(realWps[realWps.length - 1].name);
      }
    }
    onCoordsReady(index, pts, { startName, endName });
  }, [route.loading, route.coords, route.waypoints, index]);

  if (route.loading || (!route.geoJson && !route.waypoints)) return null;
  // b99.9.60 — When the user hovers a different segment in the legend
  // we completely hide this segment's rendering (polyline, direction
  // arrows, intermediate markers and tooltips). Returning `null` is
  // cleaner than opacity 0 because Leaflet still hits visually-empty
  // layers for tooltip events.
  if (hidden) return null;

  return (
    <>
      {route.geoJson && (
        <GeoJSON
          key={`seg-${index}-${route.type}`}
          data={route.geoJson}
          style={{ color, weight: 4, opacity }}
          pointToLayer={(f, ll) => L.circleMarker(ll, { radius: 0, fillOpacity: 0 })}
        />
      )}
      {route.geoJson && route.coords?.length >= 2 && (
        <DirectionArrows coords={route.coords} compact={compactMarkers} />
      )}
      {/* For precomputed segments we already drew the geoJson polyline;
          we still want the waypoint markers but NOT a second RoutedLine
          (which would double-draw the route line).
          b99.10.03 — also skip when type is "gpx": this draws the
          polyline from the source file, and rendering an extra
          RoutedLine through `localities` would overlay a wrong straight-
          ish route through unrelated cities. The waypoint markers
          (block below) still get rendered. */}
      {route.waypoints && route.waypoints.length >= 2
        && route.type !== "precomputed"
        && route.type !== "gpx" && (
        <RoutedLine waypoints={route.waypoints} color={color} opacity={opacity} compact={compactMarkers} />
      )}
      {/* b99.9.63 — Render every NON-via waypoint of this segment as a
          small `segmentIcon` pin with its location-name tooltip. This
          is what was missing in the aggregator map: each individual
          segment map already showed all its stops (SHELL, Faro de
          Corrubedo, Restaurante A Lareira, ...) but the MAPA TOTAL was
          showing only the polyline + trip-level start/finish. Via-
          shaping waypoints (Google `!3m4!1m2` blocks) are still drawn
          by `RoutedLine`/`ViaShapingDots` so we skip them here.
          b99.9.64 — Deduplication. The parent skips the global trip
          start (= waypoint[0] of segment 0) and trip end (= last
          waypoint of last segment) with door/flag icons. Junction
          points between segments (last waypoint of seg N = first
          waypoint of seg N+1) would otherwise be drawn TWICE (once
          per segment that touches them). We accept two boolean
          props `skipFirstWaypoint` / `skipLastWaypoint` that the
          parent uses to render each waypoint EXACTLY once. */}
      {route.waypoints && route.waypoints.length >= 2 && showWaypoints && (() => {
        const realWps = route.waypoints.filter(w => !(w.via === true || w.isViaPoint === true));
        const startIdx = skipFirstWaypoint && realWps.length > 0 ? 1 : 0;
        const endIdx = skipLastWaypoint && realWps.length > 0 ? realWps.length - 1 : realWps.length;
        const visibleWps = realWps.slice(startIdx, endIdx);
        // b99.9.66 — use the compact `dotIcon` on the minimized inline
        // map (the teardrop dominates the 250-px-tall canvas) and
        // keep the canonical `segmentIcon` in fullscreen where space
        // is generous. Start/finish boundary markers (door / flag)
        // are handled separately by the parent and never use this
        // path, so they stay unchanged in both modes.
        const icon = compactMarkers ? dotIcon(color) : segmentIcon(color);
        const tooltipOffset = compactMarkers ? [0, -10] : [0, -24];
        return visibleWps.map((wp, i) => {
          const label = getDisplayName(wp.name) || `Ponto ${startIdx + i + 1}`;
          return (
            <Marker key={`wp-${index}-${startIdx + i}`} position={[wp.lat, wp.lng]} icon={icon}>
              <MapTooltip direction="top" offset={tooltipOffset} opacity={0.95} className="map-tooltip-compact">
                <strong>{label}</strong>
              </MapTooltip>
            </Marker>
          );
        });
      })()}
      {/* b99.9.59 — Per-segment start/end markers REMOVED. The trip-
          level boundary markers are now rendered by the parent
          `TripOverviewMap` so the user only sees ONE start at the
          first segment's start and ONE finish at the last segment's
          end (or a single bicolor circular icon when they coincide).
          b99.9.60 — When hovering a segment in the legend the parent
          renders THAT segment's start/finish in place of the trip-
          level pair. */}
      {route.waypoints && route.waypoints.length === 1 && (
        <Marker position={[route.waypoints[0].lat, route.waypoints[0].lng]} icon={segmentIcon(color)}>
          <MapTooltip direction="top" offset={[0, -24]} opacity={0.95} className="map-tooltip-compact">
            <strong>{getDisplayName(route.waypoints[0].name) || `Etapa ${index + 1}`}</strong>
          </MapTooltip>
        </Marker>
      )}
      {/* b99.11.0 — Manual POIs (paragens) as unified square pins.
          These are distinct from routing waypoints and are filtered by
          the "Mostrar no mapa" per-type sub-filters (hiddenPoiCats). */}
      {(() => {
        // Chaves de coordenada dos extremos DESTA etapa (1.o/ultimo
        // waypoint real). Usamos os waypoints crus (não a polyline
        // "snapped") porque os PDIs partilham as MESMAS coordenadas dos
        // waypoints — assim a paragem sem categoria que coincide com o
        // Início/Fim/junção não fica empilhada por cima do pino verde/
        // bandeira (bug "caixa cinzenta", 2026-06-18).
        const kKey = (lat, lng) => `${Number(lat).toFixed(4)}|${Number(lng).toFixed(4)}`;
        const wpBoundary = new Set();
        const realWps = (route.waypoints || []).filter((w) => !(w.via === true || w.isViaPoint === true));
        if (realWps.length > 0) {
          const a = realWps[0], b = realWps[realWps.length - 1];
          wpBoundary.add(kKey(a.lat, a.lng));
          wpBoundary.add(kKey(b.lat, b.lng));
        }
        if (boundaryKeys) boundaryKeys.forEach((k) => wpBoundary.add(k));
        return (Array.isArray(segment.pois) ? segment.pois : [])
          .filter((poi) => poi && Number.isFinite(poi.lat) && Number.isFinite(poi.lng)
            && !(hiddenPoiCats && hiddenPoiCats.has(poi.category || "other"))
            && !wpBoundary.has(kKey(poi.lat, poi.lng)))
          .map((poi, i) => (
            <Marker key={`poi-${index}-${poi.id || i}`} position={[poi.lat, poi.lng]} icon={poiIcon(poi.category)}>
              <MapTooltip direction="top" offset={[0, -28]} opacity={0.95} className="map-tooltip-compact">
                <strong>{poi.name}</strong>
              </MapTooltip>
            </Marker>
          ));
      })()}
    </>
  );
}

// b99.9.56 — Sidebar block that lists the "Pontos" of a single segment.
// Uses the same `useSegmentRoute` hook as the map layer so we always
// show exactly the points that the map drew. Hovering a row publishes
// the point to the parent (for the highlight ring on the map).
function SegmentSidebarBlock({ segment, color, index, title, onHoverPoint, onHoverSegment, activeIdx = null }) {
  const { t } = useTranslation("common");
  const route = useSegmentRoute(segment);
  const distanceKm = segment.route_geometry?.distance_km
    || (route.coords && route.coords.length > 1
      ? (() => {
        let s = 0;
        for (let i = 1; i < route.coords.length; i++) {
          const a = route.coords[i - 1], b = route.coords[i];
          const dLat = (b[0] - a[0]) * Math.PI / 180;
          const dLon = (b[1] - a[1]) * Math.PI / 180;
          const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
          s += 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(x)));
        }
        return s;
      })()
      : 0);
  const durationMin = (segment.route_geometry?.duration_min)
    || (segment.duration_hours ? segment.duration_hours * 60 : 0);

  const points = useMemo(() => {
    if (route.waypoints && route.waypoints.length > 0) {
      return route.waypoints.map((wp) => ({
        lat: wp.lat,
        lng: wp.lng,
        name: getDisplayName(wp.name),
        via: wp.via === true || wp.isViaPoint === true,
      }));
    }
    return [];
  }, [route.waypoints]);

  // b99.9.95/.96 — Click-toggle do filtro de etapa (paridade com o
  // chip da legenda inline). Click no header: liga/desliga filtro.
  // Quando uma etapa está activa, as outras colapsam (só header).
  const isActive = activeIdx === index;
  const isCollapsed = activeIdx !== null && !isActive;
  return (
    <div className={`rounded-md border bg-card overflow-hidden ${isActive ? "border-orange-500" : "border-border"}`}>
      <div
        role="button"
        tabIndex={0}
        className={`flex items-stretch gap-0 cursor-pointer transition-colors ${isCollapsed ? "" : "border-b border-border/60"} ${isActive ? "bg-orange-500/15" : "bg-muted/30 hover:bg-orange-500/10"}`}
        onClick={() => onHoverSegment && onHoverSegment(isActive ? null : index)}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && onHoverSegment) {
            e.preventDefault();
            onHoverSegment(isActive ? null : index);
          }
        }}
        data-testid={`trip-overview-sidebar-segment-${index}`}
      >
        {/* b99.10.58 — Faixa lateral com a cor exacta da polyline no
            mapa para reforçar a associação visual etapa ↔ rota. Mesmo
            tratamento do Editor (Ferramentas). Contém um chevron que
            indica se os pontos estão expandidos (default ou etapa
            activa) ou colapsados (outra etapa activa). */}
        <span
          className="flex items-center justify-center px-1.5 flex-shrink-0"
          style={{
            background: color,
            boxShadow: "inset -1px 0 0 0 rgba(0,0,0,0.25)",
          }}
          aria-hidden
        >
          <ChevronRight
            className={`h-3.5 w-3.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)] transition-transform ${isCollapsed ? "" : "rotate-90"}`}
          />
        </span>
        <div className="flex-1 min-w-0 px-2.5 py-1.5">
          <div className="text-xs font-semibold truncate" title={title}>{title}</div>
          <div className="flex gap-2 text-[10px] text-muted-foreground font-mono mt-0.5">
            {distanceKm > 0 && <span>{distanceKm.toFixed(1)} km</span>}
            {durationMin > 0 && (
              <span>{Math.floor(durationMin / 60)}h{String(Math.round(durationMin % 60)).padStart(2, "0")}</span>
            )}
          </div>
        </div>
      </div>
      {!isCollapsed && (points.length > 0 ? (
        <ul className="py-1 px-1 space-y-0.5" data-testid={`overview-segment-points-${index}`}>
          {points.map((p, i) => {
            const label = p.via ? t("map.sidebar.via_point", "Ponto de forma") : p.name || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`;
            return (
              <li
                key={`pt-${index}-${i}`}
                className={`flex items-center gap-1.5 text-[10.5px] rounded px-1 py-0.5 cursor-pointer ${
                  p.via
                    ? "text-muted-foreground/60 hover:bg-muted/40 italic"
                    : "text-muted-foreground hover:bg-orange-500/10 hover:text-foreground"
                }`}
                onMouseEnter={() => onHoverPoint({ lat: p.lat, lng: p.lng, name: p.name })}
                onMouseLeave={() => onHoverPoint(null)}
                title={label}
              >
                {p.via ? (
                  <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-white border border-muted-foreground/60 flex-shrink-0" />
                ) : (
                  <MapPin className="h-3 w-3 text-orange-500/80 flex-shrink-0" />
                )}
                <span className="truncate flex-1">{label}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="px-2.5 py-1.5 text-[10px] text-muted-foreground italic">
          {route.loading ? t("map.sidebar.loading_points", "A carregar pontos…") : t("map.sidebar.no_points", "Sem pontos para listar")}
        </div>
      ))}
    </div>
  );
}

// ─── Exported reusable component ───
export function TripOverviewMap({
  segments = [],
  getSegmentTitle,
  getSegmentFullTitle,
  tripId = null,
  tripName = null,
  className = "",
  inlineHeightPx = 250,
  inlineHeightPxMobile = null,
}) {
  // Load both "common" (default) and "tools" — the POI category labels in the
  // "Mostrar no mapa" menu use t(`editor.poi.categories.${cat}`, { ns: "tools" }).
  // Without declaring "tools" here it is lazy/unloaded on the passeio page, so
  // the labels fell back to the raw English keys (viewpoint, parking, ...).
  const { t } = useTranslation(["common", "tools"]);
  const [fullscreen, setFullscreen] = useState(false);
  const [allCoords, setAllCoords] = useState([]);
  // b99.9.100 — counter that, when incremented, triggers MapResizeHandler to
  // re-fit the map to the full route bounds. Used by the "Recenter" button
  // so users on different parts of the world can snap back to the trip.
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  // b99.9.94 — per-segment coords (object keyed by idx) to support
  // click-filter re-fit and per-segment elevation panel.
  const [coordsBySegment, setCoordsBySegment] = useState({});
  const coordsRef = useRef({});
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(0);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  // b99.9.36 — tooltip + arrows toggles via Context.
  // b99.9.65 — Default `false` (OFF in inline view). A `useEffect`
  // below syncs the toggle to the fullscreen mode default (ON when
  // maximised, OFF when minimised). Trip-level start/finish tooltips
  // use `alwaysVisible` so they remain visible even when OFF.
  const { visible: tooltipsVisible, toggle: toggleTooltips, setVisible: setTooltipsVisible } = useTooltipToggle(false);
  const { visible: arrowsVisible, toggle: toggleArrows } = useArrowsToggle();
  // b99.10.41 (2026-05-21) — Visibility toggles for boundaries and
  // per-segment waypoints. Aplicados via `MapVisibilityMenu` no Controls.
  const [showStartEnd, setShowStartEnd] = useState(true);
  const [showWaypoints, setShowWaypoints] = useState(true);
  // POI type filter — Set of HIDDEN categories. Empty = show all.
  const [hiddenPoiCats, setHiddenPoiCats] = useState(() => new Set());
  // b99.9.56 (2026-05-15) — fullscreen layout parity with RouteMapViewer
  // and the Routes Editor. Layer selector + altitude toggle + sidebar
  // hover state.
  const [mapView, setMapView] = useState("2d");
  // b99.9.56/.90 — Bottom elevation panel: profile XOR histogram,
  // controlled by `activePanel`. Default "profile" so users see the
  // altitude graph on first open (parity with previous behaviour).
  const [activePanel, setActivePanel] = useState("profile");
  const [sidebarHover, setSidebarHover] = useState(null);
  // b99.9.68 — hovered point on the elevation profile (parity with
  // Editor de Rotas).
  const [hoveredCoord, setHoveredCoord] = useState(null);
  // Per-segment server elevation tracks, keyed by segment index.
  const [elevationsBySegment, setElevationsBySegment] = useState({});

  // b99.9.76 — Mobile detection para suportar altura inline diferente
  // em mobile (default 70% da altura desktop quando inlineHeightPxMobile
  // não é explicitamente passado).
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = () => setIsMobileViewport(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const effectiveInlineHeight = isMobileViewport
    ? (inlineHeightPxMobile ?? Math.round(inlineHeightPx * 0.7))
    : inlineHeightPx;

  // Filter segments that have route data
  const routeSegments = useMemo(() =>
    segments.filter(s => s.gpx_file || s.google_maps_url || (s.localities || []).some(l => l.lat && l.lng)),
    [segments]
  );

  // Distinct POI categories present across the trip — drives the
  // "Mostrar no mapa" type sub-filters (only categories actually used).
  const presentPoiCats = useMemo(() => {
    const present = new Set();
    for (const s of segments) {
      for (const p of (Array.isArray(s.pois) ? s.pois : [])) {
        present.add(p.category || "other");
      }
    }
    return POI_CATEGORY_KEYS.filter((k) => present.has(k));
  }, [segments]);

  // Items appended to the visibility menu — one checkbox per present
  // POI category (coloured swatch + translated label).
  const poiFilterItems = useMemo(() => {
    if (presentPoiCats.length === 0) return [];
    return [
      { key: "sep-poi", separator: true },
      ...presentPoiCats.map((cat) => ({
        key: `poi-${cat}`,
        testId: `trip-overview-poi-${cat}`,
        label: (
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: POI_CATEGORY_COLORS[cat] || POI_CATEGORY_COLORS.other }} />
            {t(`editor.poi.categories.${cat}`, { ns: "tools", defaultValue: cat })}
          </span>
        ),
        checked: !hiddenPoiCats.has(cat),
        onCheckedChange: (v) => setHiddenPoiCats((prev) => {
          const next = new Set(prev);
          if (v) next.delete(cat); else next.add(cat);
          return next;
        }),
      })),
    ];
  }, [presentPoiCats, hiddenPoiCats, t]);

  // b99.10.04 (2026-05-19) — Phase 1 of map unification: elevation
  // polling moved to the shared `useTripAllSegmentsElevation` hook.
  // Same source of truth as `RouteMapViewer` so future changes to
  // status handling / cooldown propagate to both views automatically.
  const elevSegmentsKey = useMemo(() => routeSegments.map((seg, idx) => {
    const originalIdx = segments.findIndex(s => (s.segment_id && s.segment_id === seg.segment_id) || s === seg);
    return { key: idx, index: originalIdx };
  }).filter(s => s.index >= 0), [routeSegments, segments]);
  const { elevationsBySegment: hookElevationsBySegment } = useTripAllSegmentsElevation({
    tripId,
    segments: elevSegmentsKey,
    // Stable reset key — when the list of segments truly changes, refetch.
    resetKey: elevSegmentsKey.map(s => `${s.key}:${s.index}`).join("|"),
  });
  useEffect(() => {
    setElevationsBySegment(hookElevationsBySegment);
  }, [hookElevationsBySegment]);

  // b99.9.59 — Trip-level boundary markers. Single start at the first
  // segment's first coord, single finish at the last segment's last
  // coord. If they coincide (within ~0.001 deg ≈ 110m), collapse to
  // a single bicolor circular icon. This applies for both N-segment
  // trips (only trip-level markers, not per-segment) AND single-
  // segment trips (showing that segment's start + end). The logic is
  // identical because a 1-segment trip has firstCoords == lastCoords.
  const [boundaries, setBoundaries] = useState(null);
  // b99.9.60 — Per-segment endpoints, used to render that single
  // segment's start/finish (instead of the trip-level pair) when the
  // user hovers a row in the legend.
  const [segmentEndpoints, setSegmentEndpoints] = useState({});
  // b99.9.61 — Names of the first/last real waypoint per segment.
  // Drives the start/finish tooltips (location, not segment title).
  const namesRef = useRef({});

  // Coordenadas dos extremos (Início/Fim trip-level + início/fim de cada
  // etapa = cobre junções entre etapas). As paragens que coincidam com
  // estes pontos não são desenhadas pela camada de PDIs para não empilhar
  // um pin de categoria por cima do pino verde/bandeira (bug 2026-06-18).
  const boundaryKeys = useMemo(() => {
    const k = (lat, lng) => `${Number(lat).toFixed(4)}|${Number(lng).toFixed(4)}`;
    const s = new Set();
    Object.values(segmentEndpoints || {}).forEach((ep) => {
      if (ep?.start) s.add(k(ep.start.lat, ep.start.lng));
      if (ep?.end) s.add(k(ep.end.lat, ep.end.lng));
    });
    if (boundaries?.start) s.add(k(boundaries.start.lat, boundaries.start.lng));
    if (boundaries?.end) s.add(k(boundaries.end.lat, boundaries.end.lng));
    return s;
  }, [segmentEndpoints, boundaries]);

  const handleCoordsReady = useCallback((idx, pts, names = {}) => {
    coordsRef.current[idx] = pts;
    namesRef.current[idx] = names;
    loadedRef.current++;
    // Per-segment endpoints — used when the legend is hovered.
    if (pts && pts.length >= 2) {
      const s = { lat: pts[0][0], lng: pts[0][1] };
      const e = { lat: pts[pts.length - 1][0], lng: pts[pts.length - 1][1] };
      const isCircular = (
        Math.abs(s.lat - e.lat) < 0.001 &&
        Math.abs(s.lng - e.lng) < 0.001
      );
      setSegmentEndpoints((prev) => ({
        ...prev,
        [idx]: {
          start: s,
          end: e,
          isCircular,
          startName: names.startName || null,
          endName: names.endName || null,
        },
      }));
    }
    if (loadedRef.current >= routeSegments.length) {
      const flat = Object.values(coordsRef.current).flat();
      setAllCoords(flat);
      // b99.9.94 — snapshot per-segment coords for click-filter re-fit.
      setCoordsBySegment({ ...coordsRef.current });
      setLoading(false);
      // Compute trip-level boundary markers.
      const firstSegCoords = coordsRef.current[0];
      const lastIdx = routeSegments.length - 1;
      const lastSegCoords = coordsRef.current[lastIdx];
      if (firstSegCoords?.length && lastSegCoords?.length) {
        const start = { lat: firstSegCoords[0][0], lng: firstSegCoords[0][1] };
        const end = {
          lat: lastSegCoords[lastSegCoords.length - 1][0],
          lng: lastSegCoords[lastSegCoords.length - 1][1],
        };
        const isCircular = (
          Math.abs(start.lat - end.lat) < 0.001 &&
          Math.abs(start.lng - end.lng) < 0.001
        );
        setBoundaries({
          start,
          end,
          isCircular,
          startName: namesRef.current[0]?.startName || null,
          endName: namesRef.current[lastIdx]?.endName || null,
        });
      }
    }
  }, [routeSegments.length]);

  // Reset when segments change
  useEffect(() => {
    coordsRef.current = {};
    namesRef.current = {};
    loadedRef.current = 0;
    setLoading(true);
    setAllCoords([]);
    setBoundaries(null);
    setSegmentEndpoints({});
    if (routeSegments.length === 0) setLoading(false);
  }, [routeSegments.length]);

  // Close fullscreen on Escape
  useEffect(() => {
    if (!fullscreen) return;
    const h = (e) => { if (e.key === "Escape") setFullscreen(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [fullscreen]);

  // b99.9.65 — Sync the tooltip toggle to the mode default whenever
  // the user enters/exits fullscreen. Inline view → OFF (only the
  // trip-level start/finish labels stay visible thanks to their
  // `alwaysVisible` flag). Fullscreen → ON (all stops labelled).
  useEffect(() => {
    setTooltipsVisible(fullscreen);
  }, [fullscreen, setTooltipsVisible]);

  // b99.9.56/.94 — Concatenated elevation across segments for the
  // bottom panel. Each segment's coords are appended in order. When a
  // specific segment is filtered via the legend (inline) or sidebar
  // (fullscreen) click, the panel shows only THAT segment's elevation.
  //
  // 2026-06-04 — We attach a scaled cumulative `distance_km` to every
  // point so ElevationProfileWeb uses the REAL road distance (sum of
  // segment distances) for its X-axis instead of the haversine of the
  // server-downsampled elevation samples (which underestimated the
  // total, e.g. 2315 km vs the trip's 2383 km). Within each segment the
  // per-point haversine is scaled to that segment's known road distance,
  // then offset by the running total of previous segments.
  const aggregatedElevation = useMemo(() => {
    const hav = (a, b) => {
      const R = 6371;
      const dLat = ((b.lat - a.lat) * Math.PI) / 180;
      const dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const la1 = (a.lat * Math.PI) / 180;
      const la2 = (b.lat * Math.PI) / 180;
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };
    const out = [];
    let baseKm = 0;
    routeSegments.forEach((seg, idx) => {
      if (hoveredIdx !== null && hoveredIdx !== idx) return;
      const segCoords = elevationsBySegment[idx];
      if (!Array.isArray(segCoords) || segCoords.length === 0) return;
      const segDistKm = seg?.route_geometry?.distance_km ?? seg?.distance_km ?? null;
      // Per-point cumulative haversine within the segment.
      const raw = [0];
      for (let i = 1; i < segCoords.length; i++) {
        raw.push(raw[i - 1] + hav(segCoords[i - 1], segCoords[i]));
      }
      const rawTotal = raw[raw.length - 1] || 0;
      const scale = segDistKm && rawTotal > 0 ? segDistKm / rawTotal : 1;
      segCoords.forEach((c, i) => {
        out.push({ ...c, distance_km: baseKm + raw[i] * scale, segIndex: idx });
      });
      baseKm += segDistKm && rawTotal > 0 ? segDistKm : rawTotal;
    });
    return out;
  }, [elevationsBySegment, routeSegments, hoveredIdx]);

  const hasElevationData = aggregatedElevation.some(c => typeof c.alt === "number" && c.alt !== 0);

  // 2026-06-09 — Whether the bottom altitude panel is ACTUALLY rendered.
  // Mirrors the render conditions below. The elevation data loads async,
  // so `hasElevationData` flips false→true AFTER the initial fit; when it
  // does, the map column shrinks. Including this in `panelKey` makes
  // MapResizeHandler re-fit so the route re-centers instead of getting
  // clipped behind the panel.
  const bottomPanelVisible =
    hasElevationData &&
    (activePanel === "profile" || activePanel === "histogram") &&
    !(isMobileViewport && !fullscreen);

  // b99.9.94 — quando uma etapa está filtrada (click no chip da
  // legenda / sidebar), o re-fit do mapa usa só os pontos dessa
  // etapa para dar zoom focado.
  const effectiveFitCoords = useMemo(() => {
    if (hoveredIdx !== null && Array.isArray(coordsBySegment[hoveredIdx])) {
      return coordsBySegment[hoveredIdx];
    }
    return allCoords;
  }, [hoveredIdx, coordsBySegment, allCoords]);

  if (routeSegments.length === 0) return null;

  // ─── Sidebar (fullscreen only) ───
  const sidebarContent = !fullscreen ? null : (
    <aside
      className="w-72 flex-shrink-0 border-r border-border bg-muted/30 overflow-y-auto"
      data-testid="trip-overview-fullscreen-sidebar"
    >
      <div className="p-3 space-y-2.5">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
          {t("trip.overview_map.segments", t("map.sidebar.segments", "Etapas"))} ({routeSegments.length})
        </div>
        {routeSegments.map((seg, i) => {
          const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          const title = getSegmentFullTitle ? getSegmentFullTitle(seg, i) : (getSegmentTitle ? getSegmentTitle(seg, i) : `Etapa ${i + 1}`);
          return (
            <SegmentSidebarBlock
              key={seg.segment_id || `seg-${i}`}
              segment={seg}
              color={color}
              index={i}
              title={title}
              onHoverPoint={setSidebarHover}
              onHoverSegment={setHoveredIdx}
              activeIdx={hoveredIdx}
            />
          );
        })}
      </div>
    </aside>
  );

  const mapContent = (
    <MapTooltipsContext.Provider value={tooltipsVisible}>
    <MapArrowsContext.Provider value={arrowsVisible}>
    <div
      className={fullscreen ? "fixed inset-0 z-[9999] bg-background flex flex-col" : `relative rounded-xl overflow-hidden border border-border flex flex-col ${className}`}
      style={fullscreen ? undefined : { height: `${effectiveInlineHeight}px` }}
      data-testid="trip-overview-map"
    >
      {/* b99.9.82 — Topbar fullscreen extraído para Controls.jsx. */}
      {fullscreen && (
        <TripOverviewControls
          variant="fullscreen"
          tripName={tripName}
          mapView={mapView}
          setMapView={setMapView}
          tooltipsVisible={tooltipsVisible}
          toggleTooltips={toggleTooltips}
          arrowsVisible={arrowsVisible}
          toggleArrows={toggleArrows}
          showStartEnd={showStartEnd}
          setShowStartEnd={setShowStartEnd}
          showWaypoints={showWaypoints}
          setShowWaypoints={setShowWaypoints}
          poiFilterItems={poiFilterItems}
          hasElevationData={hasElevationData}
          activePanel={activePanel}
          setActivePanel={setActivePanel}
          onRecenter={() => setRecenterTrigger((n) => n + 1)}
          onCloseFullscreen={() => setFullscreen(false)}
        />
      )}

      {/* MAIN AREA — sidebar (fullscreen only) + map column. */}
      <div className={fullscreen ? "flex flex-1 min-h-0 overflow-hidden" : "contents"}>
        {sidebarContent}
        <div className={fullscreen ? "relative flex-1 min-h-0 overflow-hidden flex flex-col" : "contents"}>

      {/* b99.9.82 — Cluster inline extraído para Controls.jsx.
          b99.10.09 — agora também recebe `mapView`/`setMapView` para
          mostrar o seletor de camadas (Mapa/Satélite/Terreno) inline. */}
      {!fullscreen && (
        <TripOverviewControls
          variant="inline"
          mapView={mapView}
          setMapView={setMapView}
          tooltipsVisible={tooltipsVisible}
          toggleTooltips={toggleTooltips}
          arrowsVisible={arrowsVisible}
          toggleArrows={toggleArrows}
          showStartEnd={showStartEnd}
          setShowStartEnd={setShowStartEnd}
          showWaypoints={showWaypoints}
          setShowWaypoints={setShowWaypoints}
          poiFilterItems={poiFilterItems}
          hasElevationData={hasElevationData}
          activePanel={activePanel}
          setActivePanel={setActivePanel}
          onRecenter={() => setRecenterTrigger((n) => n + 1)}
          onOpenFullscreen={() => setFullscreen(true)}
        />
      )}

      {/* Legend — INLINE mode only. Fullscreen surfaces the segment
          list in the sidebar instead.
          2026-06-09 — Legenda VERTICAL no canto superior esquerdo (o
          zoom +/- passou para a direita, libertando o canto esquerdo).  */}
      {!fullscreen && routeSegments.length > 1 && (
        <div className="absolute z-[1000] bg-background/90 backdrop-blur-sm rounded-md border border-border px-2 py-1.5 flex flex-col items-start gap-y-0.5 top-2 left-2 max-w-[55%] max-h-[calc(100%-1rem)] overflow-y-auto">
          {routeSegments.map((seg, i) => {
            const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
            const title = getSegmentTitle ? getSegmentTitle(seg, i) : `Etapa ${i + 1}`;
            const fullTitle = getSegmentFullTitle ? getSegmentFullTitle(seg, i) : title;
            const dimmed = hoveredIdx !== null && hoveredIdx !== i;
            const active = hoveredIdx === i;
            // b99.9.94 — Click-toggle filter (em vez de hover) — fica
            // "sticky" até nova interacção. Funciona em mobile (touch)
            // e desktop (click). Mesmo chip clicado uma 2ª vez desliga
            // o filtro.
            return (
              <span
                key={seg.segment_id || i}
                role="button"
                tabIndex={0}
                className={`flex items-center gap-1 text-[11px] whitespace-nowrap cursor-pointer select-none transition-opacity duration-200 rounded px-0.5 py-0 ${active ? "ring-1 ring-orange-500/60" : ""}`}
                style={{ opacity: dimmed ? 0.3 : 1 }}
                title={fullTitle}
                onClick={() => setHoveredIdx((cur) => (cur === i ? null : i))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setHoveredIdx((cur) => (cur === i ? null : i));
                  }
                }}
                data-testid={`trip-overview-legend-chip-${i}`}
              >
                <span className="w-3 h-1 rounded-full" style={{ backgroundColor: color }} />
                {title}
              </span>
            );
          })}
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 z-[999] flex items-center justify-center bg-muted/30 pointer-events-none">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}

      {/* Map */}
      <div className={`relative flex-1 min-h-0 ${fullscreen ? "" : "trip-overview-inline-map"}`}>
        <MapContainer
          center={[39.5, -8.0]}
          zoom={7}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
          zoomControl={false}
          zoomAnimation={false}
          fadeAnimation={false}
        >
          {/* b99.9.56 — Zoom +/- sempre à DIREITA (topright), igual ao
              Editor de Rotas + mapas de etapa. No inline é deslocado
              para baixo do cluster de botões via CSS
              (.trip-overview-inline-map .leaflet-top.leaflet-right). */}
          <ZoomControl position="topright" />
          <MapTileLayer view={mapView} />
          <MapResizeHandler fullscreen={fullscreen} fitCoords={effectiveFitCoords} panelKey={`${activePanel || ""}|${hoveredIdx ?? "all"}|r${recenterTrigger}|p${bottomPanelVisible ? 1 : 0}`} />

          {routeSegments.map((seg, i) => {
            const isHovered = hoveredIdx === i;
            const isLast = i === routeSegments.length - 1;
            // b99.9.64 — Deduplication rules:
            //   • Always skip the first waypoint of every segment. For
            //     segment 0 it equals the trip start (rendered as
            //     startIcon door at parent level); for any other
            //     segment it equals the previous segment's LAST
            //     waypoint (already drawn by that segment).
            //   • Skip the last waypoint only for (a) the last segment
            //     in default mode — covered by trip finishIcon — or
            //     (b) when the user is HOVERING this segment — the
            //     parent renders this segment's own start/finish then.
            return (
              <SegmentLayerWithCoords
                key={seg.segment_id || i}
                segment={seg}
                color={SEGMENT_COLORS[i % SEGMENT_COLORS.length]}
                index={i}
                onCoordsReady={handleCoordsReady}
                opacity={hoveredIdx === null ? 0.85 : 1}
                hidden={hoveredIdx !== null && hoveredIdx !== i}
                skipFirstWaypoint
                skipLastWaypoint={isLast || isHovered}
                compactMarkers
                showWaypoints={showWaypoints}
                hiddenPoiCats={hiddenPoiCats}
                boundaryKeys={boundaryKeys}
              />
            );
          })}

          {/* b99.9.59 — Trip-level boundary markers (start + finish, or
              single bicolor circular icon if they coincide). Replaces
              the per-segment markers that previously cluttered every
              segment join.
              b99.9.60 — Hidden while the user is hovering a specific
              segment in the legend (we show that segment's own
              endpoints instead, see the block below).
              b99.9.61 — Tooltip text = LOCATION name (first/last real
              waypoint), not trip/segment title. Falls back to a
              generic "Início" / "Fim" when the name isn't available. */}
          {hoveredIdx === null && showStartEnd && boundaries && boundaries.isCircular && (
            <Marker position={[boundaries.start.lat, boundaries.start.lng]} icon={circularIcon}>
              <MapTooltip direction="top" offset={[0, -40]} opacity={0.95} className="map-tooltip-compact">
                <strong>{boundaries.startName || boundaries.endName || t("map.sidebar.start_finish", "Partida / Chegada")}</strong>
              </MapTooltip>
            </Marker>
          )}
          {hoveredIdx === null && showStartEnd && boundaries && !boundaries.isCircular && (
            <>
              <Marker position={[boundaries.start.lat, boundaries.start.lng]} icon={startIcon}>
                <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact">
                  <strong>{boundaries.startName || t("map.sidebar.start", "Início")}</strong>
                </MapTooltip>
              </Marker>
              <Marker position={[boundaries.end.lat, boundaries.end.lng]} icon={finishIcon}>
                <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact">
                  <strong>{boundaries.endName || t("map.sidebar.end", "Fim")}</strong>
                </MapTooltip>
              </Marker>
            </>
          )}

          {/* b99.9.60 — Per-segment start / finish markers shown ONLY
              while the user is hovering a row in the legend filter.
              Replaces the trip-level pair so the focused segment's
              context is clear. Circular collapse (start ≈ end) still
              applies per segment.
              b99.9.61 — Tooltip text = LOCATION name of that
              segment's first/last real waypoint (not segment title). */}
          {hoveredIdx !== null && showStartEnd && segmentEndpoints[hoveredIdx] && (() => {
            const { start, end, isCircular, startName, endName } = segmentEndpoints[hoveredIdx];
            if (isCircular) {
              return (
                <Marker key={`seg-bound-circ-${hoveredIdx}`} position={[start.lat, start.lng]} icon={circularIcon}>
                  <MapTooltip direction="top" offset={[0, -40]} opacity={0.95} className="map-tooltip-compact">
                    <strong>{startName || endName || "Partida / Chegada"}</strong>
                  </MapTooltip>
                </Marker>
              );
            }
            return (
              <>
                <Marker key={`seg-bound-start-${hoveredIdx}`} position={[start.lat, start.lng]} icon={startIcon}>
                  <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact">
                    <strong>{startName || t("map.sidebar.start", "Início")}</strong>
                  </MapTooltip>
                </Marker>
                <Marker key={`seg-bound-end-${hoveredIdx}`} position={[end.lat, end.lng]} icon={finishIcon}>
                  <MapTooltip direction="top" offset={[0, -36]} opacity={0.95} className="map-tooltip-compact">
                    <strong>{endName || t("map.sidebar.end", "Fim")}</strong>
                  </MapTooltip>
                </Marker>
              </>
            );
          })()}

          {allCoords.length > 0 && <FitAllBounds allCoords={effectiveFitCoords} fullscreen={fullscreen} />}

          {/* Hover highlight ring driven by the sidebar list. */}
          {sidebarHover && (
            <HoverHighlight
              point={sidebarHover}
              label={sidebarHover?.name || null}
              showTooltip={!tooltipsVisible}
            />
          )}

          {/* b99.9.68 — Floating hover marker driven by the elevation
              profile (parity with Editor de Rotas / RouteMapViewer). */}
          {hoveredCoord && (
            <Marker
              position={[hoveredCoord.lat, hoveredCoord.lng]}
              icon={hoverIcon}
              interactive={false}
            />
          )}
        </MapContainer>
      </div>

      {/* b99.9.82/.90/.93 — Painel de altitude: profile XOR histogram.
          Mutuamente exclusivos via `activePanel`. Mobile inline:
          escondidos (os toggles também estão escondidos lá, portanto
          o utilizador não tem como controlá-los). */}
      {hasElevationData && activePanel === "profile" && !(isMobileViewport && !fullscreen) && (
        <TripOverviewElevationPanel
          coordinates={aggregatedElevation}
          height={getMapCfg(fullscreen).elevationHeight}
          onHoverPoint={setHoveredCoord}
          onClose={() => setActivePanel(null)}
          fullscreen={fullscreen}
        />
      )}
      {hasElevationData && activePanel === "histogram" && !(isMobileViewport && !fullscreen) && (
        <TripOverviewHistogramPanel
          coordinates={aggregatedElevation}
          height={getMapCfg(fullscreen).elevationHeight}
          onClose={() => setActivePanel(null)}
          fullscreen={fullscreen}
        />
      )}

        </div>{/* /map column */}
      </div>{/* /main area */}

      {/* Maximized view only — slim brand footer bar, aligned with the
          Route Editor fullscreen footer. Pairs with the logo in the top
          bar to brand the standalone canvas. */}
      {fullscreen && (
        <div
          className="shrink-0 flex items-center justify-center px-3 py-1.5 border-t border-border bg-card/95 backdrop-blur-sm text-[11px] text-muted-foreground"
          data-testid="trip-overview-fullscreen-footer"
        >
          <span>
            ©MotoTrips ·{" "}
            <a
              href="https://www.mototrips.net"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
              data-testid="trip-overview-fullscreen-footer-link"
            >
              www.mototrips.net
            </a>
          </span>
        </div>
      )}
    </div>
    </MapArrowsContext.Provider>
    </MapTooltipsContext.Provider>
  );

  return fullscreen ? createPortal(mapContent, document.body) : mapContent;
}

export default TripOverviewMap;
